import type { MonthChildRow, MonthDay } from '../../../shared/api/attendance'

/**
 * The month grid's arithmetic, kept out of the component so it can be tested.
 *
 * 🔴 CALENDAR CODE IS WHERE OFF-BY-ONES LIVE, and every one of them is visible
 * to the reader as a wrong date rather than as a crash. The month's first day
 * lands on the wrong weekday column, a 31-day month loses its last row, the year
 * boundary walks backwards into the wrong December — all of them render happily.
 * So the four functions that decide them are pure and covered.
 *
 * 🔴 UTC THROUGHOUT, MATCHING WHAT THE SERVER STORES. `Absence.day` is
 * *"midnight UTC of the day in question"* and the API returns `YYYY-MM-DD`
 * strings bucketed on UTC. Parsing those with `new Date('2026-08-01')` is
 * already UTC, but `getDay()`/`getDate()` are LOCAL — so for a reader west of
 * Greenwich the 1st would be drawn under the wrong weekday and every child's
 * absence would shift a column. Every read here is the `getUTC*` form.
 */

/** `2026-08` → `2026-07`. */
export function previousMonth(month: string): string {
  const [year, mon] = month.split('-').map(Number)
  /* ⚠️ `Date.UTC` rolls January back into the previous December on its own, so
     there is no year-boundary branch here to get wrong. */
  const at = new Date(Date.UTC(year, mon - 2, 1))
  return at.toISOString().slice(0, 7)
}

/** `2026-12` → `2027-01`. */
export function nextMonth(month: string): string {
  const [year, mon] = month.split('-').map(Number)
  const at = new Date(Date.UTC(year, mon, 1))
  return at.toISOString().slice(0, 7)
}

/** The month a date falls in, in UTC. */
export function monthOf(at: Date): string {
  return at.toISOString().slice(0, 7)
}

/** `2026-08` → `August 2026`, in the reader's locale. */
export function monthLabel(month: string): string {
  const at = new Date(`${month}-01T00:00:00.000Z`)
  if (Number.isNaN(at.getTime())) return month
  return at.toLocaleDateString(undefined, {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  })
}

/**
 * Which weekday column a `YYYY-MM-DD` belongs in, Monday first.
 *
 * ⚠️ MONDAY FIRST, NOT SUNDAY. `toLocaleDateString`'s week start varies by
 * locale and the grid's header row is fixed, so the two must agree on one rule
 * rather than each picking its own — a school week that starts on Monday is the
 * one the register is taken against.
 */
export function columnOf(day: string): number {
  const at = new Date(`${day}T00:00:00.000Z`)
  /* getUTCDay: 0 = Sunday. Shift so Monday is 0 and Sunday is 6. */
  return (at.getUTCDay() + 6) % 7
}

/**
 * The month laid out as weeks of seven, with `null` for the leading and
 * trailing blanks.
 *
 * 🔴 BLANKS RATHER THAN THE NEIGHBOURING MONTHS' DATES. A calendar that spills
 * 29–31 July into the first row of August invites a teacher to read an absence
 * against a day this view does not hold the data for. Empty cells cannot be
 * misread.
 */
export function weeksOf(days: readonly MonthDay[]): (MonthDay | null)[][] {
  if (days.length === 0) return []

  const cells: (MonthDay | null)[] = []
  for (let i = 0; i < columnOf(days[0].day); i += 1) cells.push(null)
  cells.push(...days)
  /* Pad the final row so every week is seven wide and the grid cannot ragged. */
  while (cells.length % 7 !== 0) cells.push(null)

  const weeks: (MonthDay | null)[][] = []
  for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7))
  return weeks
}

/** The day-of-month number a cell shows. */
export function dayNumber(day: string): number {
  return new Date(`${day}T00:00:00.000Z`).getUTCDate()
}

/**
 * How heavily a day should be tinted, 0–1.
 *
 * 🔴 SCALED AGAINST THE CLASS, NOT AGAINST THE BUSIEST DAY. "Six children away"
 * means one thing in a class of eight and another in a class of thirty, and a
 * scale that normalised to the month's worst day would paint a quiet month's
 * two-absence Tuesday as solid red. `childCount` is the only denominator that
 * carries meaning here — OD-39/OD-72, no count without one.
 *
 * ⚠️ A DAY WITH NO MARKS RETURNS 0 AND MUST BE DRAWN AS BLANK, not as the
 * palest tint — see `MonthDay.recorded`. Nothing is known about it.
 */
export function intensity(day: MonthDay, childCount: number): number {
  if (!day.recorded || childCount <= 0) return 0
  return Math.min(1, day.absent / childCount)
}

/** Who was where on one day, as far as the register can honestly say. */
export interface DayRoster {
  absent: MonthChildRow[]
  /**
   * 🔴 "PRESENT" IS AN INFERENCE, NOT A RECORD, AND THE UI MUST SAY SO.
   *
   * `Absence` stores absences only — there is no row anywhere meaning "this
   * child was here". So this is *"on the register now, and not marked absent on
   * that day"*, which is the strongest claim the data supports and is wrong in
   * one direction that matters: a child who joined the class after the day in
   * question appears in it, and a child who has since left is missing from it
   * entirely. `Player` carries no per-class enrolment date — `moveClass` moves a
   * child between sections without one — so there is no honest way to
   * reconstruct the register as it stood, and inventing one would be worse than
   * naming the limit.
   */
  present: MonthChildRow[]
}

/**
 * Split the class for one day.
 *
 * 🔴 IT REFUSES TO ANSWER FOR A DAY WITH NO MARKS, and that refusal is the
 * whole point. `recorded: false` means a day nobody took the register, a
 * holiday, or a day everyone was there — all identical in the database. Naming
 * twenty-four children "present" on a Sunday would be the convincing invention
 * `AttendanceMonthPanel`'s header exists to prevent, so the caller gets `null`
 * and draws a sentence instead of two lists.
 */
export function rosterOn(
  children: readonly MonthChildRow[],
  day: MonthDay | null,
): DayRoster | null {
  if (day === null || !day.recorded) return null

  const absent: MonthChildRow[] = []
  const present: MonthChildRow[] = []
  for (const child of children) {
    if (child.absentDays.includes(day.day)) absent.push(child)
    else present.push(child)
  }
  return { absent, present }
}
